<template>
  <div>
    <section v-if="loading" class="section">
      <div class="content has-text-grey has-text-centered">
        <p>Carregando licitantes...</p>
      </div>
    </section>

    <section v-else-if="!bidders.length" class="section">
      <div class="content has-text-grey has-text-centered">
        <p>Nenhum licitante.</p>
      </div>
    </section>

    <b-table v-else hoverable :data="bidders">
      <b-table-column label="Fornecedor" v-slot="props">
        {{ formatBidder(props.row) }}
      </b-table-column>

      <b-table-column centered label="Porte" v-slot="props">
        {{ formatCompanySize(props.row.porte) }}
      </b-table-column>

      <b-table-column centered label="Percentual" v-slot="props" :visible="highestDiscount">
        {{ `${formatarPorcentagem(props.row.percentual || 0)}%` }}
      </b-table-column>

      <b-table-column
        centered
        label="Valor"
        v-slot="props"
        :visible="!highestDiscount || !!(highestDiscount && item.referencia && item.total)"
      >
        {{ props.row.valor != null ? formatarValor(props.row.valor) : 0 }}
      </b-table-column>

      <b-table-column label="Status" v-slot="props">
        <b-tag :type="statusType(props.row.habilitacao)">
          {{ statusLabel(props.row.habilitacao) }}
        </b-tag>
      </b-table-column>

      <b-table-column label="Ações" v-slot="props">
        <button
          class="button is-primary is-small is-outlined"
          v-if="canManage"
          @click.prevent.stop="viewDocuments(props.row)"
        >
          Ver Documentos
        </button>
        <button
          class="button is-danger is-small is-outlined"
          style="margin-left: 2%"
          v-if="canManage && canUndo(props.row)"
          :class="{ 'is-loading': undoLoadingBidderId === props.row.fornecedorId }"
          :disabled="undoLoadingBidderId != null"
          @click.prevent.stop="undoQualification(props.row)"
        >
          Desfazer
        </button>
      </b-table-column>
    </b-table>

    <b-modal
      has-modal-card
      :active.sync="displayUndoConfirmation"
      :onCancel="clearUndoTarget"
      @confirm="confirmUndo"
    >
      <AppUndoConfirmation
        v-if="displayUndoConfirmation"
        :items="undoEligibleItems"
        :loading="undoConfirming"
        :mobile="mobile"
        warning-text="Esta ação irá desfazer a habilitação ou inabilitação deste licitante. Ele voltará sem estado definido (Pendente)."
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import AppUndoConfirmation from '@/components/AppUndoConfirmation';
import { getOfertasItem } from '@/resources/ofertasCompra';
import { formatarPorcentagem, formatarValor } from '@/utils/format';
import { funcoes } from '@/utils/enumerados';

export default {
  name: 'QualificationAnalysis',
  components: { AppUndoConfirmation },
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      offers: [],
      undoLoadingBidderId: null,
      displayUndoConfirmation: false,
      undoConfirming: false,
      pendingUndoRow: null,
      undoEligibleItems: [],
      formatarPorcentagem,
      formatarValor,
    };
  },
  computed: {
    ...mapGetters('app', { mobile: 'mobile' }),
    ...mapGetters({
      auction: 'obterOfertaCompra',
      userFunction: 'getFunction',
    }),
    highestDiscount() {
      return this.auction?.tipo_negociacao?.id === 3;
    },
    canManage() {
      return (
        this.userFunction === funcoes.PREGOEIRO ||
        this.userFunction === funcoes.AUTORIDADE ||
        this.userFunction === funcoes['AGENTE-CONTRATACAO'] ||
        this.userFunction === funcoes['AGENTE-CONTRATACAO-SUBSTITUTO']
      );
    },
    bidders() {
      const byFornecedor = new Map();

      this.offers
        .filter(offer => offer.status === 'VALIDO' && offer.fornecedorId)
        .forEach(offer => {
          const current = byFornecedor.get(offer.fornecedorId);
          if (!current) {
            byFornecedor.set(offer.fornecedorId, offer);
            return;
          }

          if (this.highestDiscount) {
            if ((offer.percentual || 0) > (current.percentual || 0)) {
              byFornecedor.set(offer.fornecedorId, offer);
            }
            return;
          }

          if (
            offer.valor != null &&
            (current.valor == null || Number(offer.valor) < Number(current.valor))
          ) {
            byFornecedor.set(offer.fornecedorId, offer);
          }
        });

      return [...byFornecedor.values()].sort((a, b) => {
        if (this.highestDiscount) {
          return (b.percentual || 0) - (a.percentual || 0);
        }
        return (a.valor || 0) - (b.valor || 0);
      });
    },
  },
  methods: {
    ...mapActions(['previewUndoAuctionItems', 'undoAuctionItems', 'atualizarHabilitacaoLances']),
    ...mapActions('requiredDocuments', ['fetchRequiredDocuments']),
    ...mapActions('auctionsDocuments', ['fetchAuctionBidderDocuments']),
    formatCompanySize(porte) {
      if (porte === 'COOPERATIVA_PREFERENCIA') return 'COOPERATIVA PREFERÊNCIA';
      return porte || '';
    },
    formatBidder(row) {
      if (row.denominacaoSocial) {
        return `(${row.fornecedor}) ${row.denominacaoSocial}`;
      }
      return row.fornecedor || '';
    },
    statusLabel(habilitacao) {
      if (habilitacao === true) return 'Habilitado';
      if (habilitacao === false) return 'Inabilitado';
      return 'Pendente';
    },
    statusType(habilitacao) {
      if (habilitacao === true) return 'is-success';
      if (habilitacao === false) return 'is-danger';
      return 'is-light';
    },
    canUndo(row) {
      return row.habilitacao === true || row.habilitacao === false;
    },
    clearUndoTarget() {
      this.displayUndoConfirmation = false;
      this.pendingUndoRow = null;
      this.undoEligibleItems = [];
      this.undoLoadingBidderId = null;
      this.undoConfirming = false;
    },
    mapUndoItem(previewItem) {
      return {
        itemId: previewItem.itemId,
        identifier: previewItem.identifier,
        description: this.item?.item?.descricao || '',
        actionLabel: previewItem.actionLabel || '',
        bidderAlias: previewItem.bidderAlias || '',
        bidderSocialDenomination: previewItem.bidderSocialDenomination || '',
        cascadeActions: previewItem.cascadeActions || [],
      };
    },
    async loadBidders() {
      if (!this.auction?.id || !this.item?.id) return;
      this.loading = true;
      try {
        const { data } = await getOfertasItem(this.auction.id, this.item.id);
        this.offers = data?.data || [];
      } catch (error) {
        this.offers = [];
        this.$alerta('Erro ao carregar licitantes do item', 'is-danger');
      } finally {
        this.loading = false;
      }
    },
    async viewDocuments(row) {
      const company = {
        alias: row.fornecedor,
        bidderId: row.fornecedorId,
        socialDenomination: row.denominacaoSocial,
      };
      await Promise.all([
        this.fetchRequiredDocuments(this.auction.id),
        this.fetchAuctionBidderDocuments({
          auctionId: this.auction.id,
          bidderId: company.bidderId,
          itemId: this.item.id,
        }),
      ]);
      this.$emitBus('documentos', { item: this.item, company });
    },
    async undoQualification(row) {
      this.undoLoadingBidderId = row.fornecedorId;
      try {
        const targets = [{ itemId: this.item.id, fornecedorId: row.fornecedorId }];
        const preview = await this.previewUndoAuctionItems({
          itemIds: [this.item.id],
          targets,
        });
        if (!preview?.eligible?.length) {
          this.$alerta('Não há decisão elegível para desfazer neste licitante', 'is-warning');
          return;
        }

        this.pendingUndoRow = row;
        this.undoEligibleItems = preview.eligible.map(item => this.mapUndoItem(item));
        this.displayUndoConfirmation = true;
      } catch (error) {
        this.$alerta('Erro ao preparar desfazer da habilitação', 'is-danger');
      } finally {
        this.undoLoadingBidderId = null;
      }
    },
    async confirmUndo({ justificativa }) {
      if (!this.pendingUndoRow || !justificativa?.trim()) {
        return;
      }

      const row = this.pendingUndoRow;
      const targets = [{ itemId: this.item.id, fornecedorId: row.fornecedorId }];
      this.undoConfirming = true;

      try {
        await this.undoAuctionItems({
          itemIds: [this.item.id],
          targets,
          justificativa: justificativa.trim(),
        });
        this.atualizarHabilitacaoLances({
          itemId: this.item.id,
          fornecedorId: row.fornecedorId,
          habilitacao: null,
        });
        this.$alerta('Ação desfeita com sucesso', 'is-success');
        this.clearUndoTarget();
        await this.loadBidders();
        this.$emit('undone');
      } catch (error) {
        this.$alerta(
          error?.response?.data?.error || error?.details?.message || 'Erro ao desfazer habilitação',
          'is-danger',
        );
        this.undoConfirming = false;
      }
    },
  },
  async created() {
    await this.loadBidders();
  },
};
</script>
